import { RunState } from "./runState";
import { selectFloorTheme } from "../data/floorConfig";
import { generateFloorEncounters } from "./encounterGeneration";
import { createSeededRng } from "../utils/rng";
import { getAutomationOverride } from "../automation/automationTags";

/**
 * Initialize a floor with its configuration and generated encounters.
 * This sets up the floor config, generates encounters, and updates run state.
 *
 * @param run - Current run state (will be mutated)
 * @param floorLevel - Floor level to initialize (1, 2, or 3)
 * @param seed - Optional seed for reproducible generation (uses run.seed if not provided)
 */
export function initializeFloor(run: RunState, floorLevel: number, seed?: number): void {
  // Use provided seed, run seed, or generate new one
  const effectiveSeed = seed ?? run.seed ?? Date.now();
  run.seed = effectiveSeed;

  // Create RNG for this floor initialization
  const rng = createSeededRng(effectiveSeed + floorLevel);

  // Select floor theme from pool
  const floorConfig = selectFloorTheme(floorLevel, rng);
  run.currentFloorConfig = floorConfig;

  // Generate encounters for this floor
  const encounters = generateFloorEncounters(floorConfig, floorLevel, rng);
  run.currentFloorEncounters = encounters;

  // Set encounter tracking
  run.encountersRemaining = encounters.length;
  run.encountersCleared = 0;
}

/**
 * Advance to the next floor.
 * Increments floor counter, selects theme, generates encounters, and updates run state.
 *
 * @param run - Current run state (will be mutated)
 * @throws Error if already at max floor
 */
export function advanceToNextFloor(run: RunState): void {
  const maxFloor = 3; // Hardcoded max floor

  if (run.floor >= maxFloor) {
    throw new Error(`Cannot advance beyond floor ${maxFloor}`);
  }

  const nextFloor = run.floor + 1;
  run.floor = nextFloor;

  // Initialize the new floor
  initializeFloor(run, nextFloor);
}

/**
 * Initialize a new run with floor 1.
 * This sets up the initial floor configuration and generates encounters.
 *
 * @param run - Run state to initialize (will be mutated)
 */
export function initializeRun(run: RunState): void {
  // Check for automation overrides
  const seedOverride = getAutomationOverride<number | string>("seed");
  const floorOverride = getAutomationOverride<number>("floor");
  const encounterCountOverride = getAutomationOverride<number>("encounterCount");

  // Determine initial floor (default to 1)
  const initialFloor = floorOverride ?? 1;
  run.floor = initialFloor;

  // Determine seed (use override if provided, otherwise generate)
  let seed: number | undefined;
  if (seedOverride !== undefined) {
    seed = typeof seedOverride === "string" ? parseInt(seedOverride, 10) : seedOverride;
  } else {
    seed = Date.now();
  }

  // Initialize floor 1
  initializeFloor(run, initialFloor, seed);

  // Override encounter count if specified (for testing)
  if (encounterCountOverride !== undefined && run.currentFloorConfig) {
    // Regenerate encounters with fixed count
    const rng = createSeededRng(seed + initialFloor);
    const floorConfig = run.currentFloorConfig;

    // Create a custom RNG that will produce the desired count
    // We'll generate encounters and then trim/pad to match override
    const encounters = generateFloorEncounters(floorConfig, initialFloor, rng);

    if (encounterCountOverride < encounters.length) {
      run.currentFloorEncounters = encounters.slice(0, encounterCountOverride);
    } else if (encounterCountOverride > encounters.length) {
      // Pad with additional encounters
      const additionalNeeded = encounterCountOverride - encounters.length;
      const additionalEncounters = generateFloorEncounters(floorConfig, initialFloor, rng);
      run.currentFloorEncounters = [
        ...encounters,
        ...additionalEncounters.slice(0, additionalNeeded),
      ];
    } else {
      run.currentFloorEncounters = encounters;
    }

    run.encountersRemaining = run.currentFloorEncounters.length;
  }
}
